import { useState } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { PageHero, CTABand, Reveal, SectionHead } from "@/components/Ui";
import AvantApres from "@/components/AvantApres";
import { CHANTIER_BY_IMAGE } from "@/data/chantiers";

const categories = ["Tout", "Maçonnerie", "Rénovation", "Gros œuvre", "Béton", "Extensions", "Aménagement extérieur"];

const realisations = [
  {
    img: "/realisations/terrasse-maison-beton.webp",
    alt: "Terrasse en béton lissé réalisée par DMZ Normandie dans l'Eure",
    titre: "Terrasse en béton lissé",
    desc: "Réalisation d'une terrasse en béton destinée à créer un espace extérieur durable et esthétique.",
    travaux: "Préparation, coffrage, ferraillage, coulage et finition lissée.",
    cats: ["Béton", "Aménagement extérieur"],
  },
  {
    img: "/realisations/pignons-parpaing.webp",
    alt: "Élévation de murs en parpaings avec pignons par DMZ Normandie, maçon près de Bernay",
    titre: "Élévation parpaings & pignons",
    desc: "Montage des murs et pignons d'une construction neuve en parpaings.",
    travaux: "Élévation des murs, linteaux, chaînages et pignons.",
    cats: ["Gros œuvre", "Maçonnerie"],
  },
  {
    img: "/realisations/mur-cloture-portail.webp",
    alt: "Mur de clôture et pilier de portail maçonnés par DMZ Normandie dans l'Eure",
    titre: "Mur de clôture & pilier de portail",
    desc: "Construction d'une clôture maçonnée délimitant et sécurisant la propriété.",
    travaux: "Fondations, montage du mur, pilier de portail et enduit de finition.",
    cats: ["Maçonnerie", "Aménagement extérieur"],
  },
  {
    img: "/realisations/gros-oeuvre-batiment.webp",
    alt: "Gros œuvre d'un bâtiment en parpaings réalisé par DMZ Normandie",
    titre: "Gros œuvre — bâtiment",
    desc: "Réalisation de la structure complète d'un bâtiment : murs porteurs et ouvrages béton.",
    travaux: "Fondations, élévation des murs, poutres et ouvrages en béton.",
    cats: ["Gros œuvre"],
  },
  {
    img: "/realisations/mur-parpaing-interieur.webp",
    alt: "Montage d'un mur intérieur en parpaings par DMZ Normandie",
    titre: "Mur en parpaings — intérieur",
    desc: "Montage d'un mur intérieur en parpaings aux joints réguliers.",
    travaux: "Montage au mortier, joints et contrôle d'aplomb.",
    cats: ["Maçonnerie", "Gros œuvre"],
  },
  {
    img: "/realisations/terrasse-beton.webp",
    alt: "Terrasse en béton lissé coulée par DMZ Normandie",
    titre: "Terrasse en béton — finition soignée",
    desc: "Terrasse en béton avec bords propres et surface plane.",
    travaux: "Terrassement, coffrage, coulage et lissage.",
    cats: ["Béton", "Aménagement extérieur"],
  },
  {
    img: "/realisations/maison-contemporaine.webp",
    alt: "Construction neuve contemporaine réalisée par DMZ Normandie",
    titre: "Construction neuve contemporaine",
    desc: "Gros œuvre et maçonnerie d'une maison contemporaine.",
    travaux: "Fondations, murs et dalles.",
    cats: ["Gros œuvre", "Maçonnerie"],
  },
  {
    img: "/realisations/facade-brique-silex.webp",
    alt: "Rénovation et rejointoiement d'une façade en brique et silex par DMZ Normandie",
    titre: "Rénovation de façade — brique & silex",
    desc: "Rénovation d'une maçonnerie traditionnelle en brique et silex.",
    travaux: "Reprise de maçonnerie, rejointoiement et enduits.",
    cats: ["Rénovation", "Maçonnerie"],
  },
  {
    img: "/realisations/maison-plain-pied.webp",
    alt: "Maison plain-pied avec allée pavée réalisée par DMZ Normandie",
    titre: "Maison plain-pied & allée pavée",
    desc: "Construction d'une maison plain-pied et aménagement de son extérieur.",
    travaux: "Gros œuvre, allée en pavés et bordures.",
    cats: ["Gros œuvre", "Aménagement extérieur"],
  },
  {
    img: "/realisations/garage-carport.webp",
    alt: "Extension : garage et carport construits par DMZ Normandie",
    titre: "Garage & carport — extension",
    desc: "Construction d'un garage avec carport attenant.",
    travaux: "Fondations, murs et dalle béton.",
    cats: ["Extensions", "Maçonnerie"],
  },
  {
    img: "/realisations/gros-oeuvre-maison.webp",
    alt: "Gros œuvre d'une maison individuelle en parpaings par DMZ Normandie",
    titre: "Gros œuvre — maison individuelle",
    desc: "Élévation complète des murs d'une maison neuve sur dalle béton.",
    travaux: "Élévation des murs, linteaux, chaînages et ouvertures.",
    cats: ["Gros œuvre", "Maçonnerie"],
  },
  {
    img: "/realisations/batiment-brique-chantier.webp",
    alt: "Construction d'un bâtiment en brique par DMZ Normandie",
    titre: "Bâtiment en brique — chantier professionnel",
    desc: "Construction d'un bâtiment professionnel en brique, façades et structure.",
    travaux: "Maçonnerie brique, ouvrages béton et façades.",
    cats: ["Gros œuvre", "Maçonnerie"],
  },
  {
    img: "/realisations/maison-parpaing-plain-pied.webp",
    alt: "Élévation d'une maison plain-pied en parpaings par DMZ Normandie",
    titre: "Maison plain-pied — élévation",
    desc: "Montage des murs d'une maison plain-pied avec ses ouvertures.",
    travaux: "Élévation en parpaings, linteaux et chaînages.",
    cats: ["Gros œuvre", "Maçonnerie"],
  },
  {
    img: "/realisations/vide-sanitaire-parpaing.webp",
    alt: "Vide sanitaire et soubassements en parpaings par DMZ Normandie",
    titre: "Vide sanitaire & soubassements",
    desc: "Montage des soubassements et du vide sanitaire d'une construction neuve.",
    travaux: "Fondations, soubassements en parpaings et vide sanitaire.",
    cats: ["Gros œuvre"],
  },
  {
    img: "/realisations/gros-oeuvre-cour.webp",
    alt: "Gros œuvre d'un bâtiment avec cour en béton par DMZ Normandie",
    titre: "Bâtiment & cour béton",
    desc: "Structure en parpaings d'un bâtiment avec sa cour en béton.",
    travaux: "Élévation des murs, ouvrages béton et dalle de cour.",
    cats: ["Gros œuvre", "Béton"],
  },
  {
    img: "/realisations/renovation-facade-bernay.webp",
    alt: "Rénovation de façade en brique à Bernay par DMZ Normandie",
    titre: "Rénovation de façade — Bernay",
    desc: "Rénovation complète d'une façade en brique en centre-ville de Bernay.",
    travaux: "Reprise de maçonnerie, rejointoiement brique et remise en état de la façade.",
    cats: ["Rénovation", "Maçonnerie"],
  },
  {
    img: "/realisations/reprise-linteau-bernay.webp",
    alt: "Reprise de linteau en brique au-dessus d'une fenêtre à Bernay par DMZ Normandie",
    titre: "Reprise de linteau brique — Bernay",
    desc: "Reprise de maçonnerie au-dessus d'une fenêtre : linteau et briques recalés.",
    travaux: "Étaiement, dépose et reprise du linteau, remontée brique par brique.",
    cats: ["Rénovation", "Maçonnerie"],
  },
  {
    img: "/realisations/mur-brique-bernay.webp",
    alt: "Construction d'un mur en brique à Bernay par DMZ Normandie",
    titre: "Maçonnerie brique — Bernay",
    desc: "Montage de murs en brique avec linteaux et ouvertures, à Bernay.",
    travaux: "Élévation brique, linteaux et finition des baies.",
    cats: ["Maçonnerie", "Gros œuvre"],
  },
  {
    img: "/realisations/terrasse-carrelee-bernay.webp",
    alt: "Terrasse carrelée réalisée à Bernay par DMZ Normandie",
    titre: "Terrasse carrelée — Bernay",
    desc: "Pose d'une terrasse carrelée grand format, prête à vivre.",
    travaux: "Préparation du support, dalle et pose du carrelage extérieur.",
    cats: ["Aménagement extérieur", "Béton"],
  },
  {
    img: "/realisations/facade-decapage-bernay.webp",
    alt: "Décapage d'un mur ancien en cours de rénovation à Bernay par DMZ Normandie",
    titre: "Rénovation en cours — Bernay",
    desc: "Pendant le chantier : décapage de l'enduit dégradé et mise à nu de la maçonnerie ancienne.",
    travaux: "Décapage, diagnostic du mur et préparation avant reprise.",
    cats: ["Rénovation", "Maçonnerie"],
  },
  {
    img: "/realisations/facade-avant-bernay.webp",
    alt: "Façade latérale dégradée avant rénovation à Bernay par DMZ Normandie",
    titre: "Façade avant travaux — Bernay",
    desc: "L'état de la façade avant notre intervention : enduit dégradé et maçonnerie fatiguée.",
    travaux: "Constat et diagnostic avant décapage et rénovation.",
    cats: ["Rénovation", "Maçonnerie"],
  },
  {
    img: "/realisations/parement-brique-beuzeville-facade.webp",
    alt: "Parement en brique sur façade à Beuzeville par DMZ Normandie",
    titre: "Parement brique — Beuzeville",
    desc: "Pose d'un parement en brique sur façade, sous échafaudage, à Beuzeville.",
    travaux: "Préparation du support et pose du parement brique.",
    cats: ["Maçonnerie", "Rénovation"],
  },
  {
    img: "/realisations/parement-brique-beuzeville-pignon.webp",
    alt: "Parement brique sur pignon d'une maison à Beuzeville par DMZ Normandie",
    titre: "Pignon en parement brique — Beuzeville",
    desc: "Habillage d'un pignon complet en parement brique.",
    travaux: "Pose du parement en hauteur, finitions des rives.",
    cats: ["Maçonnerie", "Rénovation"],
  },
  {
    img: "/realisations/parement-brique-beuzeville-mur.webp",
    alt: "Détail de pose d'un parement brique à Beuzeville par DMZ Normandie",
    titre: "Parement brique — détail de pose",
    desc: "Zoom sur la pose soignée du parement : joints réguliers et alignement parfait.",
    travaux: "Pose du parement et joints.",
    cats: ["Maçonnerie"],
  },
  {
    img: "/realisations/bordures-escalier-paves-louviers.webp",
    alt: "Bordures en escalier et pavés posés à Louviers par DMZ Normandie",
    titre: "Bordures en escalier & pavés — Louviers",
    desc: "Aménagement extérieur : escalier bordé de pavés et mur de soutènement en parpaings.",
    travaux: "Pose de bordures, pavés et montage du muret.",
    cats: ["Aménagement extérieur", "Maçonnerie"],
  },
  {
    img: "/realisations/paves-conches-en-ouche.webp",
    alt: "Allée en pavés posée à Conches-en-Ouche par DMZ Normandie",
    titre: "Allée en pavés — Conches-en-Ouche",
    desc: "Pose d'une allée en pavés avec bordures béton, à Conches-en-Ouche.",
    travaux: "Préparation du lit, pose des pavés et bordures.",
    cats: ["Aménagement extérieur"],
  },
  {
    img: "/realisations/gros-oeuvre-pignons-pont-audemer.webp",
    alt: "Gros œuvre d'une maison à double pignon à Pont-Audemer par DMZ Normandie",
    titre: "Gros œuvre à double pignon — Pont-Audemer",
    desc: "Élévation complète d'une maison en parpaings à double pignon, prête pour la charpente.",
    travaux: "Fondations, élévation des murs, pignons et ouvertures.",
    cats: ["Gros œuvre", "Maçonnerie"],
  },
  {
    img: "/realisations/elevation-poutre-thiberville.webp",
    alt: "Élévation de murs en parpaings avec poutre béton à Thiberville par DMZ Normandie",
    titre: "Élévation & poutre béton — Thiberville",
    desc: "Montage des murs en parpaings avec grande poutre béton de chaînage.",
    travaux: "Élévation des murs, coffrage et coulage de la poutre béton.",
    cats: ["Gros œuvre", "Maçonnerie"],
  },
  {
    img: "/realisations/gros-oeuvre-deauville.webp",
    alt: "Gros œuvre vu du ciel : dalles et murs en parpaings à Deauville par DMZ Normandie",
    titre: "Gros œuvre — Deauville",
    desc: "Vue aérienne du chantier : dalles coulées et élévation des murs en parpaings.",
    travaux: "Fondations, dalles béton et élévation des murs.",
    cats: ["Gros œuvre", "Maçonnerie"],
  },
  {
    img: "/realisations/maison-parpaing-louviers.webp",
    alt: "Maison en parpaings avec toiture posée à Louviers par DMZ Normandie",
    titre: "Maison en parpaings — Louviers",
    desc: "Maçonnerie complète d'une maison : murs, pignons, ouvertures et poutre de façade.",
    travaux: "Élévation des murs, linteaux, poutre béton et pignons.",
    cats: ["Gros œuvre", "Maçonnerie"],
  },
];

export default function Realisations() {
  const [cat, setCat] = useState("Tout");
  const filtered = cat === "Tout" ? realisations : realisations.filter((r) => r.cats.includes(cat));

  return (
    <>
      <SEO
        title="Nos réalisations en maçonnerie | DMZ Normandie – Bernay, Eure"
        description="Chantiers réalisés par DMZ Normandie : terrasses béton, gros œuvre, murs, clôtures, rénovations et extensions à Bernay et dans l'Eure."
        path="/realisations"
      />
      <PageHero
        title="Nos réalisations"
        subtitle="Chaque photo présentée ici est un chantier réellement réalisé par DMZ Normandie. Terrasses, constructions, rénovations et extensions en Normandie."
      />

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 mb-20">
          <SectionHead overline="Avant / Après" title="La preuve par l'image" />
          <Reveal>
            <AvantApres
              avant="/realisations/facade-pendant-bernay.webp"
              apres="/realisations/facade-apres-bernay.webp"
              altAvant="Façade en brique pendant la rénovation par DMZ Normandie à Bernay"
              altApres="Façade rénovée avec encadrements en brique par DMZ Normandie à Bernay"
            />
            <div className="card-dark p-6 mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <h3 className="font-serif-display font-light text-xl text-[#F5F5F0]">Rénovation de façade — centre de Bernay</h3>
                <p className="text-sm font-light text-zinc-500 mt-1">Décapage, reprise de maçonnerie, enduit neuf et encadrements en brique refaits.</p>
              </div>
              <span className="text-[10px] tracking-[0.25em] uppercase text-[#C4A484] shrink-0">Faites glisser le curseur</span>
            </div>
          </Reveal>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal className="max-w-3xl mb-14">
            <p className="text-zinc-400 font-light leading-relaxed">
              Derrière chaque photo se cache un vrai chantier : terrasses en béton coulées et lissées,
              élévations de murs en parpaings, clôtures maçonnées, façades en brique et silex rénovées,
              garages et extensions construits dans l'Eure. Pour chaque réalisation, nous détaillons les
              travaux effectivement réalisés par nos équipes — rien d'inventé, uniquement nos chantiers.
            </p>
          </Reveal>
          <div className="flex flex-wrap gap-2 mb-12" data-testid="realisations-filters">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`filter-${c.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={`px-5 py-2.5 text-xs tracking-[0.15em] uppercase font-medium transition-colors duration-300 border ${
                  cat === c
                    ? "bg-[#C4A484] text-[#0A0A0A] border-[#C4A484]"
                    : "bg-transparent text-zinc-400 border-white/10 hover:border-[#C4A484] hover:text-[#C4A484]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="realisations-grid">
            {filtered.map((r, i) => {
              const chantier = CHANTIER_BY_IMAGE[r.img];
              return (
                <Reveal key={r.titre} delay={(i % 3) * 0.06}>
                  <figure className="group card-dark h-full" data-testid={`realisation-${i}`}>
                    <div className="overflow-hidden relative">
                      <img src={r.img} alt={r.alt} loading="lazy" className="w-full h-64 object-cover scale-[1.06] group-hover:scale-100 transition-transform duration-700" />
                      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/0 transition-colors duration-700" />
                    </div>
                    <figcaption className="p-6">
                      <div className="flex flex-wrap gap-2 mb-3">
                        {r.cats.map((c) => (
                          <span key={c} className="text-[10px] tracking-[0.2em] uppercase text-[#C4A484] border border-[#C4A484]/30 px-2.5 py-1">{c}</span>
                        ))}
                      </div>
                      <h3 className="font-serif-display font-light text-xl text-[#F5F5F0] mb-2">{r.titre}</h3>
                      <p className="text-sm font-light text-zinc-500 leading-relaxed">{r.desc}</p>
                      <p className="mt-3 text-xs font-light text-zinc-600 leading-relaxed border-l border-[#C4A484]/40 pl-3">
                        Travaux réalisés : {r.travaux}
                      </p>
                      {chantier && (
                        <Link to={`/chantiers/${chantier.slug}`} className="inline-flex items-center gap-2 mt-5 text-xs tracking-[0.25em] uppercase text-[#C4A484] hover:text-[#F5F5F0] transition-colors" data-testid={`voir-chantier-${chantier.slug}-${i}`}>
                          Voir la page chantier
                        </Link>
                      )}
                    </figcaption>
                  </figure>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
